import React, { useState, useRef } from 'react';
import { ReferenceAsset, AppSettings } from '../types';
import { fileToBase64 } from '../utils/helpers';
import { Plus, Image as ImageIcon, Layers, User, Mountain, Box } from 'lucide-react';
import AssetImportModal from './AssetImportModal';
import { AssetItem } from './AssetItem';

interface AssetSidebarProps {
  assets: ReferenceAsset[];
  settings: AppSettings;
  onAssetCreate: (newAssets: ReferenceAsset[]) => void;
  onAssetUpdate: (updatedAsset: ReferenceAsset) => void;
  onAssetDelete: (assetId: string) => void;
}

type AssetType = ReferenceAsset['type'];

const AssetSidebar: React.FC<AssetSidebarProps> = ({ assets, settings, onAssetCreate, onAssetUpdate, onAssetDelete }) => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | AssetType>('all');
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>({ name: '', keywords: '', type: 'character' });

  // Called by AssetImportModal
  const handleAssetsAdded = (newAssets: ReferenceAsset[]) => {
      onAssetCreate(newAssets);
  };

  const removeAsset = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('确定要删除此资源吗？')) {
        onAssetDelete(id);
        if (editingId === id) setEditingId(null);
    }
  };

  const startEditing = (e: React.MouseEvent, asset: ReferenceAsset) => {
    e.stopPropagation();
    setEditingId(asset.id);
    setEditForm({ 
        id: asset.id, // Include ID
        name: asset.name, 
        keywords: asset.keywords || '', 
        type: asset.type, 
        url: asset.url, 
        base64: asset.base64, 
        mimeType: asset.mimeType 
    });
  };

  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        try {
            const base64 = await fileToBase64(file);
            setEditForm((prev:any) => ({ ...prev, url: URL.createObjectURL(file), base64, mimeType: file.type }));
        } catch (err) { console.error(err); }
    }
  };

  const saveEditing = (id: string) => {
    // Construct updated asset from form
    // Note: We need to preserve the ID.
    const updatedAsset: ReferenceAsset = {
        ...editForm,
        id: id // Ensure ID matches
    };
    onAssetUpdate(updatedAsset);
    setEditingId(null);
  };

  const filteredAssets = activeTab === 'all' ? assets : assets.filter(a => a.type === activeTab);
  
  const getTypeLabel = (type: string) => {
      switch(type) { case 'character': return '角色'; case 'scene': return '场景'; case 'prop': return '道具'; case 'style': return '风格'; case 'all': return '全部'; default: return type; }
  }
  
  const TabButton = ({ id, label, icon: Icon }: { id: 'all' | AssetType, label: string, icon: any }) => (
    <button onClick={() => setActiveTab(id)} className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all ${activeTab === id ? 'bg-indigo-600 text-white shadow-md scale-105' : 'text-slate-500 hover:bg-slate-800 hover:text-slate-300'}`} title={label}><Icon size={18} /><span className="text-[9px] font-medium mt-1">{label}</span></button>
  );

  return (
    <div className="w-72 bg-slate-900 border-r border-slate-700 flex flex-col h-full shrink-0">
      <div className="p-4 border-b border-slate-700 bg-slate-800">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><ImageIcon className="w-5 h-5 text-indigo-400" /> 参考图库</h2>
        <div className="grid grid-cols-4 gap-1 mt-4 bg-slate-900 p-1 rounded-xl">
            <TabButton id="all" label="全部" icon={Layers} />
            <TabButton id="character" label="角色" icon={User} />
            <TabButton id="scene" label="场景" icon={Mountain} />
            <TabButton id="prop" label="道具" icon={Box} />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {filteredAssets.length === 0 && <div className="text-center text-slate-500 py-8 border-2 border-dashed border-slate-700 rounded-lg"><p className="text-sm">暂无 {getTypeLabel(activeTab)} 资源。</p></div>}
        {filteredAssets.map(asset => (
            <AssetItem key={asset.id} asset={asset} isEditing={editingId === asset.id} editForm={editForm} setEditForm={setEditForm} startEditing={startEditing} saveEditing={saveEditing} cancelEditing={() => setEditingId(null)} removeAsset={removeAsset} getTypeLabel={getTypeLabel} triggerFileInput={() => replaceInputRef.current?.click()} />
        ))}
      </div>
      <div className="p-4 border-t border-slate-700 bg-slate-800">
        <button onClick={() => setIsImportModalOpen(true)} className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors"><Plus size={16} /> 添加 / 生成资源</button>
      </div>
      <input type="file" accept="image/*" ref={replaceInputRef} className="hidden" onChange={handleReplaceFile} />
      <AssetImportModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onAssetsAdded={handleAssetsAdded} settings={settings} />
    </div>
  );
};
export default AssetSidebar;